import React, { useState, useMemo, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useFAQ } from '../hooks/useData';
import LoadingSpinner from './ui/LoadingSpinner';
import Pagination from './ui/Pagination';
import { FAQ_CATEGORIES } from '../utils/constants';

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeCategory, setActiveCategory] = useState(FAQ_CATEGORIES[0]);
  const { faqData, loading, error } = useFAQ();
  
  const itemsPerPage = 4;

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Reset page and open state when category changes
  useEffect(() => {
    setCurrentPage(1);
    setOpenIndex(null);
  }, [activeCategory]);

  const fallbackFAQ = [
    {
      id: 1,
      question: "Como funciona a consultoria preventiva para empresas?",
      answer: "Nossa consultoria avalia as rotinas de RH, identifica riscos trabalhistas e propõe ajustes práticos para alinhar sua empresa à legislação, reduzindo passivos financeiros.",
      category: FAQ_CATEGORIES[1]
    },
    {
      id: 2,
      question: "Quais os meus direitos em caso de assédio moral?",
      answer: "O trabalhador vítima de assédio moral tem direito a ingressar com pedido de rescisão indireta e indenização por danos morais, desde que haja provas testemunhais ou documentais das agressões psicológicas.",
      category: FAQ_CATEGORIES[0]
    },
    {
      id: 3,
      question: "Como proceder com moradores inadimplentes?",
      answer: "A cota condominial é um título executivo. Com nossa assessoria, esgotamos a via amigável e entramos com execução judicial para bloquear bens ou valores na conta do devedor de forma ágil.",
      category: FAQ_CATEGORIES[2]
    }
  ];

  const dataToUse = error || (!loading && faqData.length === 0) ? fallbackFAQ : faqData;

  const filteredData = useMemo(() => {
    return dataToUse.filter(faq => {
      // If faq has a category, match it. If not, maybe show in the first tab or a general tab. 
      // Assuming new FAQs have category matching the exact string.
      return faq.category === activeCategory || (!faq.category && activeCategory === FAQ_CATEGORIES[0]);
    });
  }, [dataToUse, activeCategory]);

  // Calcular dados paginados
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setOpenIndex(null);
  };

  return (
    <section id="faq" className="section bg-white">
      <div className="wrapper">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <h2 className="text-3xl md:text-4xl font-heading text-brand-slate mb-8 text-center">
            Dúvidas Frequentes
          </h2>

          <div className="flex flex-col md:flex-row gap-2 mb-8 bg-brand-sand/30 p-2 rounded-xl">
            {FAQ_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex-1 py-3 px-4 rounded-lg font-medium text-sm text-center transition-all duration-300 ${
                  activeCategory === cat
                    ? 'bg-brand-slate text-brand-sand shadow-md'
                    : 'text-brand-ink hover:bg-brand-sand/80'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {loading ? (
            <LoadingSpinner message="Carregando perguntas..." />
          ) : (
            <>
              <div className="space-y-4 min-h-[300px]">
                {paginatedData.length === 0 ? (
                  <p className="text-center text-brand-ink/70 py-12">Nenhuma pergunta encontrada para esta categoria.</p>
                ) : (
                  paginatedData.map((faq, index) => (
                    <motion.div
                      key={faq.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="card"
                    >
                      <button
                        onClick={() => toggleFAQ(index)}
                        className="w-full flex items-center justify-between p-6 text-left"
                      >
                        <h3 className="text-lg font-semibold text-brand-slate">
                          {faq.question}
                        </h3>
                        {openIndex === index ? (
                          <ChevronUp className="w-5 h-5 text-brand-accent flex-shrink-0 ml-4" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-brand-accent flex-shrink-0 ml-4" />
                        )}
                      </button>
                      
                      <AnimatePresence>
                        {openIndex === index && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="px-6 pb-6">
                              <p className="text-brand-ink leading-relaxed whitespace-pre-line">
                                {faq.answer}
                              </p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))
                )}
              </div>

              {/* Paginação */}
              {totalPages > 1 && (
                <div className="mt-8">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    itemsPerPage={itemsPerPage}
                    totalItems={filteredData.length}
                  />
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;